import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormGroup, AbstractControl } from "@angular/forms";
import { TitleCasePipe } from "@angular/common";
import { Observable, of, Subscription, from } from "rxjs";
import { map, catchError, switchMap } from "rxjs/operators";
import { GISPointTypes } from "../../../../../../common/models/well.model";
import { GisQueryService } from "../../../../../../gis/services/gis-query.service";
import { GisTokenService } from "../../../../../../gis/services/gis-token.service";
import { UpdatedGISValue } from "../../../../../../models/request.result";
import { LoaderService } from "../../../../../../common/services/loader.service";
import { ApplicationState, WorkflowData, WorkflowNote, WorkflowFieldData } from '../../../../../../applications/models/applications.models';
import { ApplicationSectionBaseComponent } from '../../../../../../applications/components/application-section-base/application-section-base.component';
import { AuthService } from "../../../../../../services/auth.service";


@Component({
    selector: 'location-point',
    templateUrl: './location-point.component.html',
    styleUrls: ['./location-point.component.scss'],
    providers: [GisQueryService, GisTokenService],
    standalone: false
})
export class LocationPointComponent extends ApplicationSectionBaseComponent implements OnInit, OnDestroy {



    @Input() point: GISPointTypes;


    get disableValidate(): boolean {
        return (this.application != null && this.reviewStates.includes(this.application.applicationState))
            || this.latitudeCtrl == null
            || this.latitudeCtrl.value == null
            || this.longitudeCtrl == null
            || this.longitudeCtrl.value == null
            || ((this.latitudeCtrl != null && this.storedLatitude != null && this.latitudeCtrl.value == this.storedLatitude) && (this.longitudeCtrl != null && this.storedLongitude != null && this.longitudeCtrl.value == this.storedLongitude))
    }

    get locationPoint(): string {
        let value: string = "";
        if (this.point != null) {
            switch (this.point) {
                case GISPointTypes.WellHead:
                    value = "Well Head";
                    break;
                case GISPointTypes.Heel:
                    value = "Heel";
                    break;
                case GISPointTypes.Toe:
                    value = "Toe";
                    break;
                case GISPointTypes.Target:
                    value = "Target";
                    break;
            }
        }
        return value;
    }

    get latitudeCtrl(): AbstractControl {
        if (this.form == null) return null;
        return this.form.get('latitude');
    }

    get longitudeCtrl(): AbstractControl {
        if (this.form == null) return null;
        return this.form.get('longitude');
    }

    get pointValidationRequired(): boolean {
        if (this.form == null || this.form.get('coordinatesValidated') == null) return false;
        return this.form.get('coordinatesValidated').invalid;
    }

    get relatedFields(): WorkflowFieldData[] {
        if (this.application == null) return [];
        return this.application.workflowFieldDatas.filter(f => f.formControlName.includes(this.point));
    }

    get storedLatitude(): number | null {
        let field = this.relatedFields.find(r => r.formControlName.includes("latitude"));
        if (field == null) return null;
        return field.numericData;
    }

    get storedLongitude(): number | null {
        let field = this.relatedFields.find(r => r.formControlName.includes("longitude"));
        if (field == null) return null;
        return field.numericData;
    }

    subscription: Subscription;

    types = GISPointTypes;

    validationHelpText: string = "Coordinate validation is only required when latitude and/or longitude values are altered.";

    constructor(private queryService: GisQueryService, private casePipe: TitleCasePipe, private loader: LoaderService, _auth: AuthService) {
        super(_auth);
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.form != null) {
            let runCtrl = this.form.get('queryRun');
            let validCtrl = this.form.get('coordinatesValidated');
            if (runCtrl != null) runCtrl.enable();
            if (validCtrl != null) validCtrl.enable();
        }
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    getFieldName(tail: string): string {
        return `wellInformation|points|${this.point}|${tail}`;
    }

    invalidate(): void {
        let validCtrl = this.form.get('coordinatesValidated');
        if (validCtrl != null) validCtrl.setValue(false);
        let runCtrl = this.form.get('queryRun');
        if (runCtrl != null) runCtrl.setValue(false);
        this.form.updateValueAndValidity();
    }

    queryCounty(): Observable<boolean> {
        let latitude = Number(this.form.get('latitude').value);
        let longitude = Number(this.form.get('longitude').value);
        let latLong: number[] = [latitude, longitude];
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "Counties", latLong);
        return from(query).pipe(
            map((res: any) => {
                let verified: boolean = false;
                if (res != null && res.features != null && res.features.length > 0) {
                    let resolvedCountyName: string = res.features[0].attributes["COUNTY"] || "";
                    let resolvedCountyNumber: number = parseInt(res.features[0].attributes["FIPS"], 10);
                    this.form.get('countyName').setValue(this.casePipe.transform(resolvedCountyName));
                    this.form.get('countyNumber').setValue(resolvedCountyNumber);
                    verified = true;
                }
                return verified;
            })
        );
    }

    querySectionLotTract(): Observable<boolean> {
        let sectionComponent: AbstractControl = this.form.get('section');;
        let lotComponent: AbstractControl = this.form.get('lot');;
        let tractComponent: AbstractControl = this.form.get('tract');;
        let civilTownshipNumberComponent: AbstractControl = this.form.get("civilTownshipNumber");;
        let townshipNumberComponent: AbstractControl = this.form.get('townshipNumber');
        let latitude = Number(this.form.get('latitude').value);
        let longitude = Number(this.form.get('longitude').value);
        let latLong: number[] = [latitude, longitude];
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "land_subdivisions_old", latLong);
        return from(query).pipe(      
            map((result: any) => {
                if (result != null && result.features != null && result.features.length > 0) {
                    const data = result.features[0].attributes;
                    sectionComponent = this.form.get('section');
                    lotComponent = this.form.get('lot');
                    tractComponent = this.form.get('tract');
                    if (data["SECTION_"] != null) {
                        let section = data["SECTION_"].toString().trim().length > 0 ? data["SECTION_"] : "";
                        sectionComponent.setValue(section);
                    }
                    if (data["LOT"] != null) {
                        let lot = data["LOT"].toString().trim().length > 0 ? data["LOT"] : "";
                        lotComponent.setValue(lot);
                    }
                    if (data["TRACT"] != null) {
                        let tract = data["TRACT"].toString().trim().length > 0 ? data["TRACT"] : "";
                        tractComponent.setValue(tract);
                    }
                    if (data["CVLTWP_NO"] != null) {
                        let townshipNumber = data["CVLTWP_NO"].toString().trim().length !== 0 ? data["CVLTWP_NO"] : "";
                        civilTownshipNumberComponent.setValue(townshipNumber);
                    }
                    if (data["TWP_NUM"] != null) {
                        let townshipNumber = data["TWP_NUM"].toString().trim().length !== 0 ? data["TWP_NUM"] : "";
                        townshipNumberComponent.setValue(townshipNumber);
                    }
                }
                return true;
            }),
            catchError((error) => {
                console.error(error);
                return of(false);
            })
        );
    }

    queryQuadrangle(): Observable<boolean> {
        let latitude = Number(this.form.get('latitude').value);
        let longitude = Number(this.form.get('longitude').value);
        let latLong: number[] = [latitude, longitude];
        let query: any = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "quad24k", latLong);
        return from(query).pipe(
            map((res: any) => {
                let codeComponent = this.form.get('quadrangleCode');
                let nameComponent = this.form.get('quadrangleName');
                let quadrangleName: string = nameComponent.value;
                let quadrangleCode: string = codeComponent.value;
                if (res && res.features && res.features.length > 0) {
                    quadrangleName = this.casePipe.transform(res.features[0].attributes["QUADNAME"] || "");
                    quadrangleCode = res.features[0].attributes["OGSCODE"] || "";                    
                }
                codeComponent.setValue(quadrangleCode);
                nameComponent.setValue(quadrangleName);
                return true;
            }),
            catchError((error) => {
                console.error(error);
                return of(false);
            })
        )
    }

    queryTownship(): Observable<boolean> {
        let latitude = Number(this.form.get('latitude').value);
        let longitude = Number(this.form.get('longitude').value);
        let latLong: number[] = [latitude, longitude];
        let nameComponent: AbstractControl = this.form.get('townshipName');;
        let originalTownshipName: any = nameComponent.value;
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "Townships", latLong);
        return from(query).pipe(
            map((res: any) => {
                let result: UpdatedGISValue[] = [];
                let nameResult = new UpdatedGISValue("Township Name", originalTownshipName, "");
                if (res != null && res.features != null && res.features.length > 0) {                   
                    let resolvedTownshipName: string = res.features[0].attributes["TOWNSHIP"];
                    if (resolvedTownshipName.toLocaleUpperCase() === resolvedTownshipName) {
                        resolvedTownshipName = this.casePipe.transform(resolvedTownshipName);
                    }
                    nameResult.correctedValue = resolvedTownshipName;
                    nameComponent.setValue(resolvedTownshipName);
                }
                return result;
            }),
            map(() => {
                return true;
            }),
            catchError((error) => {
                console.error(error);
                return of(false);
            })
        )
    }

    validate(): void {
        let obs$: Observable<boolean> = of(true);
        obs$ = obs$.pipe(
            switchMap((val) => {
                if (val) {
                    return this.queryCounty();
                } else {
                    return of(val);
                }
            }),
            switchMap((val) => {
                if (val) {
                    return this.queryTownship();
                } else {
                    return of(val);
                }
            }),
            switchMap((val) => {
                if (val) {
                    return this.querySectionLotTract();
                } else {
                    return of(val);
                }
            }),
            switchMap((val) => {
                if (val) {
                    return this.queryQuadrangle();
                } else {
                    return of(val);
                }
            })
        )
        this.loader.toggleLoader(true);
        this.subscription = obs$.subscribe(
            (res: boolean) => {
                this.loader.toggleLoader(false);
                let validCtrl = this.form.get('coordinatesValidated');
                if (validCtrl != null) validCtrl.setValue(res);
                let runCtrl = this.form.get('queryRun');
                if (runCtrl != null) runCtrl.setValue(res);
            },
            (error) => {
                console.error(error);
                this.loader.toggleLoader(false);
            }
        );
    }

}
